import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Pull-to-refresh hook for mobile touch devices.
 * Returns { containerRef, pullIndicator } — attach containerRef to the scrollable element,
 * render pullIndicator at the top of the content.
 */
export function usePullToRefresh(onRefresh, { threshold = 80 } = {}) {
  const [pulling, setPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(0);
  const containerRef = useRef(null);

  const handleTouchStart = useCallback((e) => {
    const el = containerRef.current;
    if (!el || el.scrollTop > 5) return;
    startY.current = e.touches[0].clientY;
    setPulling(true);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!pulling || refreshing) return;
    const el = containerRef.current;
    if (!el || el.scrollTop > 5) {
      setPullDistance(0);
      return;
    }
    const diff = e.touches[0].clientY - startY.current;
    if (diff > 0) {
      setPullDistance(Math.min(diff * 0.5, threshold * 1.5));
    }
  }, [pulling, refreshing, threshold]);

  const handleTouchEnd = useCallback(async () => {
    if (!pulling) return;
    setPulling(false);
    if (pullDistance >= threshold && !refreshing) {
      setRefreshing(true);
      try {
        await onRefresh();
      } catch (e) {
        // swallow
      }
      setRefreshing(false);
    }
    setPullDistance(0);
  }, [pulling, pullDistance, threshold, refreshing, onRefresh]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: true });
    el.addEventListener('touchend', handleTouchEnd);
    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  const isTriggered = pullDistance >= threshold;

  const pullIndicator = (pullDistance > 10 || refreshing) ? (
    <div
      className="flex items-center justify-center overflow-hidden transition-all duration-200 sm:hidden"
      style={{ height: refreshing ? 48 : pullDistance * 0.6 }}
      data-testid="pull-to-refresh-indicator"
    >
      <div className={`flex items-center gap-2 text-xs text-zinc-400 ${refreshing ? 'animate-pulse' : ''}`}>
        <svg
          className={`w-4 h-4 transition-transform duration-200 ${isTriggered || refreshing ? 'rotate-180' : ''} ${refreshing ? 'animate-spin' : ''}`}
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        >
          {refreshing ? (
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          ) : (
            <path d="M12 5v14M5 12l7-7 7 7" />
          )}
        </svg>
        <span>{refreshing ? 'Refreshing...' : isTriggered ? 'Release to refresh' : 'Pull to refresh'}</span>
      </div>
    </div>
  ) : null;

  return { containerRef, pullIndicator };
}
